import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { IFileValidator } from "@bitwarden/web-vault/app/models/interfaces/file-validator.interface";
import { fileTypeAllowed } from "@bitwarden/web-vault/app/shared/utils/file-upload/file-type";
import { pickFile } from "@bitwarden/web-vault/app/shared/utils/file-upload/pick-file";
import { FileValidator } from "@bitwarden/web-vault/app/validators/file-validator/file-validator";
import { FileUploadService } from "@bitwarden/web-vault/app/services/DataService/file-upload/file-upload.service";
import { MappedStatementRow } from "@bitwarden/web-vault/app/shared/utils/file-upload/statement-mapping.util";

@Component({
  selector: "app-file-upload",
  templateUrl: "./file-upload.component.html",
})
export class FileUploadComponent {
  @ViewChild("fileSelector") fileSelector?: ElementRef<HTMLInputElement>;

  @Input() isClickComplete = false;
  /** Mapped statement rows; when present the inline preview shows the mapped table. */
  @Input() mappedRows: MappedStatementRow[] = [];
  @Output() selectedFile = new EventEmitter<File | null>();
  /** Notifies the host that the user removed the selected file, so it can reset import state. */
  @Output() removeFile = new EventEmitter<void>();

  /** Whether the full-preview modal/sheet is open. */
  protected showFullPreview: WritableSignal<boolean> = signal<boolean>(false);

  private fileUploadService = inject(FileUploadService);
  private document = inject(DOCUMENT);

  /** True once a statement has been mapped. */
  get hasMapped(): boolean {
    return (this.mappedRows?.length ?? 0) > 0;
  }

  /** True for csv / xls / xlsx — these are mapped, never shown raw. */
  get isTabularFile(): boolean {
    return !!this.currentFile && this.fileUploadService.isTabular(this.currentFile);
  }
  protected fileName = "";
  protected fileValidator: IFileValidator;
  protected readonly fileTypeAllowed = fileTypeAllowed;
  protected currentFile: File | null = null;
  errorMessage: string | null = null;

  constructor() {
    this.fileValidator = new FileValidator();
  }

  onSuppress(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileUpload(event: Event) {
    this.onSuppress(event);
    return this.handleFileSelected(event);
  }

  onFileDrop(event: DragEvent) {
    this.onSuppress(event);
    const file = event.dataTransfer?.files[0];
    if (file) {
      const newEvent = { target: { files: [file] } };
      this.handleFileSelected(newEvent);
    }
  }

  private handleFileSelected(event: any) {
    this.errorMessage = null;
    const file = event.target.files[0];
    if (file) {
      if (!this.fileValidator.validate(file)) {
        this.errorMessage = this.fileValidator.errorMessage;
        this.fileName = this.fileValidator.fileName;
        return;
      }
      this.fileName = file.name;
      this.currentFile = file;
      this.selectedFile.emit(file);
    }
  }

  clearFileSelected(event?: Event, fileSelector?: HTMLInputElement) {
    if (event) {
      this.onSuppress(event);
    }
    this.fileName = "";
    this.currentFile = null;
    this.showFullPreview.set(false);
    // Notify the parent so the wizard doesn't keep holding the now-cleared File.
    this.selectedFile.emit(null);
    // Reset the input value too, otherwise re-selecting the same file fires no
    // `change` event and the dropzone appears to do nothing.
    const input = fileSelector ?? this.fileSelector?.nativeElement;
    if (input) {
      input.value = "";
    }
  }

  /**
   * Re-open the file picker to replace the current file. The current file/preview is
   * kept in place until a new file is actually chosen, so cancelling is non-destructive.
   */
  async replaceFile(): Promise<void> {
    const file = await pickFile(this.document, this.fileTypeAllowed);
    if (file) {
      this.handleFileSelected({ target: { files: [file] } });
    }
  }

  /** User clicked the "x" on the selected file: reset locally and let the host clear its import state. */
  onRemoveClick(event: Event) {
    this.clearFileSelected(event);
    this.removeFile.emit();
  }

  onFullPreviewClick(): void {
    this.showFullPreview.set(true);
  }

  closeFullPreview(): void {
    this.showFullPreview.set(false);
  }
}
